import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Alert,
} from '@mui/material';
import { CheckCircle as CheckIcon, PeopleAlt as PeopleIcon, Chat as ChatIcon, Flag as FlagIcon, EmojiEmotions as EmojiIcon, ArrowBack as BackIcon, SmartToy as AIIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import TagInput from './TagInput';
import type { BusinessProfileFormData } from '../../types/business';

interface ContentStrategyStepProps {
  formData: BusinessProfileFormData;
  setFormData: (data: any) => void;
  tagInputs: any;
  setTagInputs: (inputs: any) => void;
  strategyLoading: boolean;
  strategyGenerated: boolean;
  strategyError?: string;
  onGenerateStrategy: () => void;
  onSkipGeneration?: () => void;
  onNext: () => void;
  onBack: () => void;
  onTagAdd: (field: string) => void;
  onTagRemove: (field: string, index: number) => void;
  onTagInputChange: (field: string, value: string) => void;
  onTagKeyPress: (field: string, event: React.KeyboardEvent) => void;
  isValid: boolean;
  disabled?: boolean;
  quotaMessage?: string | null;
}

const ContentStrategyStep: React.FC<ContentStrategyStepProps> = ({
  formData,
  tagInputs,
  strategyLoading,
  strategyGenerated,
  strategyError,
  onGenerateStrategy,
  onSkipGeneration,
  onNext,
  onBack,
  onTagAdd,
  onTagRemove,
  onTagInputChange,
  onTagKeyPress,
  isValid,
  disabled = false,
  quotaMessage,
}) => {
  const { t } = useTranslation('profile');

  return (
    <Box>
      {strategyGenerated ? (
        <Box>
          <Grid container spacing={3}>
            {/* Target Audience */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PeopleIcon />
                    {t('businessProfile.strategy.targetAudience')} *
                  </Typography>
                  <TagInput
                    field="target_audience"
                    label={t('businessProfile.strategy.targetAudienceLabel')}
                    color="secondary"
                    tags={formData.target_audience}
                    inputValue={tagInputs.target_audience}
                    onInputChange={onTagInputChange}
                    onTagAdd={onTagAdd}
                    onTagRemove={onTagRemove}
                    onKeyPress={onTagKeyPress}
                    required
                    maxTags={5}
                    showError={!isValid && formData.target_audience.length === 0}
                    errorText={t('businessProfile.strategy.targetAudienceRequired')}
                  />
                </CardContent>
              </Card>
            </Grid>

            {/* Key Messages */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ChatIcon />
                    {t('businessProfile.strategy.keyMessages')} *
                  </Typography>
                  <TagInput
                    field="key_messages"
                    label={t('businessProfile.strategy.keyMessagesLabel')}
                    color="success"
                    tags={formData.key_messages}
                    inputValue={tagInputs.key_messages}
                    onInputChange={onTagInputChange}
                    onTagAdd={onTagAdd}
                    onTagRemove={onTagRemove}
                    onKeyPress={onTagKeyPress}
                    required
                    maxTags={5}
                    showError={!isValid && formData.key_messages.length === 0}
                    errorText={t('businessProfile.strategy.keyMessagesRequired')}
                  />
                </CardContent>
              </Card>
            </Grid>

            {/* Content Goals */}
            <Grid size={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FlagIcon />
                    {t('businessProfile.strategy.contentGoals')} *
                  </Typography>
                  <TagInput
                    field="content_goals"
                    label={t('businessProfile.strategy.contentGoalsLabel')}
                    color="warning"
                    tags={formData.content_goals}
                    inputValue={tagInputs.content_goals}
                    onInputChange={onTagInputChange}
                    onTagAdd={onTagAdd}
                    onTagRemove={onTagRemove}
                    onKeyPress={onTagKeyPress}
                    required
                    maxTags={5}
                    showError={!isValid && formData.content_goals.length === 0}
                    errorText={t('businessProfile.strategy.contentGoalsRequired')}
                  />
                </CardContent>
              </Card>
            </Grid>

            {/* Brand Voice */}
            <Grid size={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EmojiIcon />
                    {t('businessProfile.strategy.brandVoice')} *
                  </Typography>
                  <TagInput
                    field="brand_voice"
                    label={t('businessProfile.strategy.brandVoiceLabel')}
                    color="primary"
                    tags={Array.isArray(formData.brand_voice) ? formData.brand_voice.filter(tag => tag && tag.trim()) : []}
                    inputValue={tagInputs.brand_voice}
                    onInputChange={onTagInputChange}
                    onTagAdd={onTagAdd}
                    onTagRemove={onTagRemove}
                    onKeyPress={onTagKeyPress}
                    required
                    maxTags={5}
                    showError={!isValid && (!Array.isArray(formData.brand_voice) || formData.brand_voice.filter(t => t && t.trim()).length === 0)}
                    errorText={t('businessProfile.strategy.brandVoiceRequired')}
                  />
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {strategyError && (
            <Alert severity="error" sx={{ mt: 4, mb: 2 }}>
              {strategyError}
            </Alert>
          )}
          {quotaMessage && (
            <Alert severity="warning" sx={{ mt: 3, mb: 2 }}>
              {quotaMessage}
            </Alert>
          )}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between', 
            alignItems: { xs: 'stretch', sm: 'center' },
            gap: 2,
            mt: 4 
          }}>
            <Button
              variant="outlined"
              onClick={onBack}
              startIcon={<BackIcon />}
              sx={{ order: { xs: 3, sm: 1 } }}
            >
              {t('businessProfile.buttons.back')}
            </Button>
            <Button
              variant="contained"
              onClick={onGenerateStrategy}
              disabled={strategyLoading || disabled}
              startIcon={strategyLoading ? <CircularProgress size={20} color="inherit" /> : <AIIcon />}
              sx={{
                order: { xs: 2, sm: 2 },
                background: 'linear-gradient(135deg, #6C63FF 0%, #FF6CAB 100%)',
                color: 'white',
                fontWeight: 600,
                textTransform: 'none',
                px: 3,
                py: 1.5,
                borderRadius: 2,
                boxShadow: '0 4px 12px rgba(108, 99, 255, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5A52E6 0%, #E55A8A 100%)',
                  boxShadow: '0 6px 16px rgba(108, 99, 255, 0.4)',
                },
                '&:disabled': {
                  background: 'rgba(108, 99, 255, 0.5)',
                  boxShadow: 'none',
                },
              }}
            >
              {strategyLoading ? t('businessProfile.strategy.generating') : t('businessProfile.strategy.regenerateButton')}
            </Button>
            <Button
              variant="contained"
              onClick={onNext}
              disabled={!isValid}
              startIcon={<CheckIcon />}
              sx={{ 
                order: { xs: 1, sm: 3 },
                minWidth: { xs: 'auto', sm: 200 } 
              }}
            >
              {t('businessProfile.buttons.nextSelectPlatforms')}
            </Button>
          </Box>
        </Box>
      ) : (
        <Box>
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <AIIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              {t('businessProfile.strategy.title')}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 600, mx: 'auto' }}>
              {t('businessProfile.strategy.description')}
            </Typography>
            {strategyError && (
              <Alert severity="error" sx={{ mb: 2, maxWidth: 600, mx: 'auto' }}>
                {strategyError}
              </Alert>
            )}
            {quotaMessage && (
              <Alert severity="warning" sx={{ mb: 2, maxWidth: 600, mx: 'auto' }}>
                {quotaMessage}
              </Alert>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
              <Button
                variant="contained"
                size="large"
                onClick={onGenerateStrategy}
                disabled={strategyLoading || disabled}
                startIcon={strategyLoading ? <CircularProgress size={20} color="inherit" /> : <AIIcon />}
                sx={{
                  background: 'linear-gradient(135deg, #6C63FF 0%, #FF6CAB 100%)',
                  color: 'white',
                  fontWeight: 600,
                  textTransform: 'none',
                  px: 3,
                  py: 1.5,
                  borderRadius: 2,
                  boxShadow: '0 4px 12px rgba(108, 99, 255, 0.3)',
                  minWidth: 250,
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5A52E6 0%, #E55A8A 100%)',
                    boxShadow: '0 6px 16px rgba(108, 99, 255, 0.4)',
                  },
                  '&:disabled': {
                    background: 'rgba(108, 99, 255, 0.5)',
                    boxShadow: 'none',
                  },
                }}
              >
                {strategyLoading ? t('businessProfile.strategy.generating') : t('businessProfile.strategy.generateButton')}
              </Button>
              
              {/* Always show "Enter Manually" option - gives users control */}
              {onSkipGeneration && (
                <Button
                  variant="text"
                  onClick={onSkipGeneration}
                  disabled={strategyLoading}
                  sx={{ 
                    textTransform: 'none',
                    color: 'text.secondary',
                    '&:hover': {
                      color: 'primary.main',
                      backgroundColor: 'transparent',
                    }
                  }}
                >
                  {t('businessProfile.strategy.enterManually', 'or enter manually')}
                </Button>
              )}
            </Box>
          </Box>
          {strategyLoading && (
            <Alert severity="info" sx={{ mt: 3, maxWidth: 600, mx: 'auto' }}>
              {t('businessProfile.strategy.generatingAlert')}
            </Alert>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ContentStrategyStep;
